import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/core/styles";
import {
  Hash, Timer, FileText, QrCode, Camera,
  List as ListIcon, Braces, Cpu,
  ChevronRight, ArrowLeft,
  Anchor, Pin, LayoutGrid, Search,
} from "lucide-react";
import { Dialog, DialogHeader, DialogTitle, DialogBody } from "../Dialog";
import { dataService } from "../../../Services/data/data.service";
import { flowBoxDeviceStatus, externalApiTypes, externalApiEnumBehaviorTypes } from "../../../Config/constants";
import { color, radius, transition } from "../tokens";

const STEP = {
  CATEGORY: "category",
  MEASUREMENTS: "measurements",
  LISTS: "lists",
  APIS: "apis",
  FLOWBOX_DEVICES: "flowbox-devices",
  FLOWBOX_BUTTONS: "flowbox-buttons",
};

const useStyles = makeStyles(() => ({
  categoryGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 10,
    padding: "4px 0 8px",
  },

  categoryCard: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 8,
    padding: "14px 14px",
    border: `1px solid ${color.border.default}`,
    borderRadius: radius.md,
    background: color.surface.card,
    cursor: "pointer",
    textAlign: "left",
    transition: `border-color ${transition.base}, background ${transition.base}`,
    "&:hover": {
      borderColor: "#4654FB",
      background: color.surface.subtle,
    },
  },

  categoryCardDisabled: {
    opacity: 0.4,
    pointerEvents: "none",
  },

  categoryIcon: {
    color: "#4654FB",
    flexShrink: 0,
  },

  categoryTitle: {
    fontWeight: 600,
    fontSize: 13,
    color: color.text.primary,
    margin: 0,
  },

  categoryDescription: {
    fontSize: 12,
    color: color.text.secondary,
    margin: 0,
    lineHeight: 1.4,
  },

  backButton: {
    display: "flex",
    alignItems: "center",
    gap: 5,
    padding: "0 0 12px 0",
    background: "none",
    border: "none",
    cursor: "pointer",
    color: color.text.secondary,
    fontSize: 13,
    "&:hover": {
      color: color.text.primary,
    },
  },

  stepLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: color.text.muted,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    margin: "0 0 8px",
  },

  searchWrapper: {
    position: "relative",
    marginBottom: 10,
  },

  searchIcon: {
    position: "absolute",
    left: 10,
    top: "50%",
    transform: "translateY(-50%)",
    color: color.text.muted,
    pointerEvents: "none",
    display: "flex",
    alignItems: "center",
  },

  searchInput: {
    width: "100%",
    height: 36,
    paddingLeft: 34,
    paddingRight: 12,
    fontSize: 14,
    border: `1px solid ${color.border.strong}`,
    borderRadius: radius.sm,
    outline: "none",
    boxSizing: "border-box",
    color: color.text.primary,
    fontFamily: "Lato, sans-serif",
    "&:focus": {
      borderColor: "#4654FB",
    },
    "&::placeholder": {
      color: color.text.muted,
    },
  },

  itemsList: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    maxHeight: 320,
    overflowY: "auto",
  },

  item: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 12px",
    border: `1px solid ${color.border.default}`,
    borderRadius: radius.md,
    cursor: "pointer",
    background: color.surface.card,
    textAlign: "left",
    width: "100%",
    flexShrink: 0,
    transition: `border-color ${transition.base}, background ${transition.base}`,
    "&:hover": {
      borderColor: "#4654FB",
      background: color.surface.subtle,
    },
  },

  itemDisabled: {
    opacity: 0.4,
    pointerEvents: "none",
  },

  itemIcon: {
    color: "#4654FB",
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
  },

  itemContent: {
    flex: 1,
    minWidth: 0,
  },

  itemTitle: {
    fontWeight: 500,
    fontSize: 14,
    color: color.text.primary,
    margin: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  itemDescription: {
    fontSize: 12,
    color: color.text.secondary,
    margin: "2px 0 0",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  itemChevron: {
    color: color.text.muted,
    flexShrink: 0,
  },

  emptyState: {
    textAlign: "center",
    padding: "32px 16px",
    color: color.text.secondary,
    fontSize: 14,
  },

  loadingState: {
    textAlign: "center",
    padding: "32px 16px",
    color: color.text.muted,
    fontSize: 14,
  },
}));

const TypePickerDialog = ({
  open,
  onClose,
  onSelect,
  companyId,
  lists,
  apis,
  hasAnchorApi,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const [step, setStep] = useState(STEP.CATEGORY);
  const [searchQuery, setSearchQuery] = useState("");
  const [flowBoxDevices, setFlowBoxDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [buttonMappings, setButtonMappings] = useState([]);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [loadingMappings, setLoadingMappings] = useState(false);

  useEffect(() => {
    if (!open) {
      setStep(STEP.CATEGORY);
      setSearchQuery("");
      setSelectedDevice(null);
      setButtonMappings([]);
      setFlowBoxDevices([]);
    }
  }, [open]);

  useEffect(() => {
    setSearchQuery("");
  }, [step]);

  const filteredLists = (lists || []).filter((l) => !companyId || l.companyId === companyId);
  const filteredApis = (apis || []).filter((a) => !companyId || a.companyId === companyId);

  const handleCategorySelect = async (category) => {
    if (category === STEP.FLOWBOX_DEVICES) {
      setStep(STEP.FLOWBOX_DEVICES);
      if (!companyId) return;
      setLoadingDevices(true);
      try {
        const devices = await dataService.getFlowBoxDevicesByCompanyId(companyId);
        setFlowBoxDevices((devices || []).filter((d) => d.statusId === flowBoxDeviceStatus.ACTIVE));
      } catch {
        setFlowBoxDevices([]);
      } finally {
        setLoadingDevices(false);
      }
    } else {
      setStep(category);
    }
  };

  const handleDeviceSelect = async (device) => {
    setSelectedDevice(device);
    setStep(STEP.FLOWBOX_BUTTONS);
    setLoadingMappings(true);
    try {
      const mappings = await dataService.getButtonMappingsByDeviceId(device.id);
      setButtonMappings((mappings || []).filter((m) => m.isActive));
    } catch {
      setButtonMappings([]);
    } finally {
      setLoadingMappings(false);
    }
  };

  const handleSelect = (value) => {
    onSelect(value);
    onClose();
  };

  const goBack = () => {
    if (step === STEP.FLOWBOX_BUTTONS) {
      setStep(STEP.FLOWBOX_DEVICES);
    } else {
      setStep(STEP.CATEGORY);
    }
  };

  const matchesSearch = (text) =>
    !searchQuery || (text || "").toLowerCase().includes(searchQuery.toLowerCase());

  const getApiTypeIcon = (api) => {
    if (api.externalApiTypeId === externalApiTypes.ENUM) {
      if (api.externalApiEnumBehaviorTypeId === externalApiEnumBehaviorTypes.ANCHOR)
        return <Anchor size={16} />;
      if (api.externalApiEnumBehaviorTypeId === externalApiEnumBehaviorTypes.STICKY)
        return <Pin size={16} />;
      return <ListIcon size={16} />;
    }
    switch (api.externalApiTypeId) {
      case externalApiTypes.STOPWATCH: return <Timer size={16} />;
      case externalApiTypes.NUMERIC: return <Hash size={16} />;
      default: return <Braces size={16} />;
    }
  };

  const getButtonMappingIcon = (mapping) => {
    switch (mapping.externalApiTypeId) {
      case externalApiTypes.STOPWATCH: return <Timer size={16} />;
      case externalApiTypes.NUMERIC: return <Hash size={16} />;
      default: return <Braces size={16} />;
    }
  };

  const measurementTypes = [
    {
      id: 1,
      name: t("tableDragResources.typeCounter"),
      description: "Count individual events or occurrences",
      icon: <Hash size={16} />,
    },
    {
      id: 2,
      name: t("tableDragResources.typeStopwatch"),
      description: "Track time-based measurements",
      icon: <Timer size={16} />,
    },
    {
      id: 3,
      name: t("tableDragResources.typeFreeText"),
      description: "Record freeform text entries",
      icon: <FileText size={16} />,
    },
    {
      id: 5,
      name: t("tableDragResources.typeScanner"),
      description: "Scan QR codes or barcodes",
      icon: <QrCode size={16} />,
    },
    {
      id: 6,
      name: t("tableDragResources.typePicture"),
      description: "Capture photos during measurement",
      icon: <Camera size={16} />,
    },
  ];

  const renderSearchField = (placeholder) => (
    <div className={classes.searchWrapper}>
      <span className={classes.searchIcon}>
        <Search size={14} />
      </span>
      <input
        className={classes.searchInput}
        type="text"
        placeholder={placeholder}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        autoFocus
      />
    </div>
  );

  const renderContent = () => {
    switch (step) {
      case STEP.CATEGORY:
        return (
          <>
            <p className={classes.stepLabel}>Choose a category</p>
            <div className={classes.categoryGrid}>
              <button
                className={classes.categoryCard}
                onClick={() => handleCategorySelect(STEP.MEASUREMENTS)}
              >
                <LayoutGrid size={26} className={classes.categoryIcon} />
                <p className={classes.categoryTitle}>Measurement input</p>
                <p className={classes.categoryDescription}>
                  Counter, Stopwatch, Free text, Scanner, Picture
                </p>
              </button>

              <button
                className={`${classes.categoryCard} ${filteredLists.length === 0 ? classes.categoryCardDisabled : ""}`}
                onClick={() => filteredLists.length > 0 && handleCategorySelect(STEP.LISTS)}
              >
                <ListIcon size={26} className={classes.categoryIcon} />
                <p className={classes.categoryTitle}>Lists</p>
                <p className={classes.categoryDescription}>
                  Searchable data lists for structured input
                </p>
              </button>

              <button
                className={`${classes.categoryCard} ${filteredApis.length === 0 ? classes.categoryCardDisabled : ""}`}
                onClick={() => filteredApis.length > 0 && handleCategorySelect(STEP.APIS)}
              >
                <Braces size={26} className={classes.categoryIcon} />
                <p className={classes.categoryTitle}>External APIs</p>
                <p className={classes.categoryDescription}>
                  Connected API endpoints that push measurements
                </p>
              </button>

              <button
                className={`${classes.categoryCard} ${!companyId ? classes.categoryCardDisabled : ""}`}
                onClick={() => companyId && handleCategorySelect(STEP.FLOWBOX_DEVICES)}
              >
                <Cpu size={26} className={classes.categoryIcon} />
                <p className={classes.categoryTitle}>FlowBox</p>
                <p className={classes.categoryDescription}>
                  Physical button devices connected to this company
                </p>
              </button>
            </div>
          </>
        );

      case STEP.MEASUREMENTS:
        return (
          <>
            <button className={classes.backButton} onClick={goBack}>
              <ArrowLeft size={13} /> Back
            </button>
            <p className={classes.stepLabel}>Measurement input</p>
            <div className={classes.itemsList}>
              {measurementTypes.map((type) => (
                <button
                  key={type.id}
                  className={classes.item}
                  onClick={() => handleSelect(`type:${type.id}`)}
                >
                  <span className={classes.itemIcon}>{type.icon}</span>
                  <span className={classes.itemContent}>
                    <p className={classes.itemTitle}>{type.name}</p>
                    <p className={classes.itemDescription}>{type.description}</p>
                  </span>
                  <ChevronRight size={15} className={classes.itemChevron} />
                </button>
              ))}
            </div>
          </>
        );

      case STEP.LISTS: {
        const visibleLists = filteredLists.filter((l) => matchesSearch(l.name));
        return (
          <>
            <button className={classes.backButton} onClick={goBack}>
              <ArrowLeft size={13} /> Back
            </button>
            <p className={classes.stepLabel}>Lists</p>
            {filteredLists.length === 0 ? (
              <div className={classes.emptyState}>No data lists available.</div>
            ) : (
              <>
                {renderSearchField("Search lists...")}
                <div className={classes.itemsList}>
                  {visibleLists.length === 0 ? (
                    <div className={classes.emptyState}>No results.</div>
                  ) : (
                    visibleLists.map((list) => (
                      <button
                        key={list.id}
                        className={classes.item}
                        onClick={() => handleSelect(`list:${list.id}`)}
                      >
                        <span className={classes.itemIcon}>
                          <ListIcon size={16} />
                        </span>
                        <span className={classes.itemContent}>
                          <p className={classes.itemTitle}>{list.name}</p>
                        </span>
                        <ChevronRight size={15} className={classes.itemChevron} />
                      </button>
                    ))
                  )}
                </div>
              </>
            )}
          </>
        );
      }

      case STEP.APIS: {
        const visibleApis = filteredApis.filter((a) => matchesSearch(a.name));
        return (
          <>
            <button className={classes.backButton} onClick={goBack}>
              <ArrowLeft size={13} /> Back
            </button>
            <p className={classes.stepLabel}>External APIs</p>
            {filteredApis.length === 0 ? (
              <div className={classes.emptyState}>No external APIs available.</div>
            ) : (
              <>
                {renderSearchField("Search APIs...")}
                <div className={classes.itemsList}>
                  {visibleApis.length === 0 ? (
                    <div className={classes.emptyState}>No results.</div>
                  ) : (
                    visibleApis.map((api) => {
                      const isDisabled =
                        hasAnchorApi &&
                        api.externalApiEnumBehaviorTypeId === externalApiEnumBehaviorTypes.ANCHOR;
                      return (
                        <button
                          key={api.id}
                          className={`${classes.item} ${isDisabled ? classes.itemDisabled : ""}`}
                          onClick={() => !isDisabled && handleSelect(`api:${api.id}`)}
                        >
                          <span className={classes.itemIcon}>{getApiTypeIcon(api)}</span>
                          <span className={classes.itemContent}>
                            <p className={classes.itemTitle}>{api.name}</p>
                          </span>
                          <ChevronRight size={15} className={classes.itemChevron} />
                        </button>
                      );
                    })
                  )}
                </div>
              </>
            )}
          </>
        );
      }

      case STEP.FLOWBOX_DEVICES: {
        const visibleDevices = flowBoxDevices.filter((d) =>
          matchesSearch(d.displayName || `Device ${d.id}`)
        );
        return (
          <>
            <button className={classes.backButton} onClick={goBack}>
              <ArrowLeft size={13} /> Back
            </button>
            <p className={classes.stepLabel}>FlowBox devices</p>
            {loadingDevices ? (
              <div className={classes.loadingState}>Loading devices...</div>
            ) : flowBoxDevices.length === 0 ? (
              <div className={classes.emptyState}>
                No active FlowBox devices found for this company.
              </div>
            ) : (
              <>
                {flowBoxDevices.length > 4 && renderSearchField("Search devices...")}
                <div className={classes.itemsList}>
                  {visibleDevices.length === 0 ? (
                    <div className={classes.emptyState}>No results.</div>
                  ) : (
                    visibleDevices.map((device) => (
                      <button
                        key={device.id}
                        className={classes.item}
                        onClick={() => handleDeviceSelect(device)}
                      >
                        <span className={classes.itemIcon}>
                          <Cpu size={16} />
                        </span>
                        <span className={classes.itemContent}>
                          <p className={classes.itemTitle}>
                            {device.displayName || `Device ${device.id}`}
                          </p>
                        </span>
                        <ChevronRight size={15} className={classes.itemChevron} />
                      </button>
                    ))
                  )}
                </div>
              </>
            )}
          </>
        );
      }

      case STEP.FLOWBOX_BUTTONS:
        return (
          <>
            <button className={classes.backButton} onClick={goBack}>
              <ArrowLeft size={13} />{" "}
              {selectedDevice?.displayName || "Back"}
            </button>
            <p className={classes.stepLabel}>Select a button</p>
            {loadingMappings ? (
              <div className={classes.loadingState}>Loading button mappings...</div>
            ) : buttonMappings.length === 0 ? (
              <div className={classes.emptyState}>
                No active button mappings configured for this device.
              </div>
            ) : (
              <div className={classes.itemsList}>
                {buttonMappings.map((mapping) => (
                  <button
                    key={mapping.id}
                    className={classes.item}
                    onClick={() => handleSelect(`api:${mapping.externalApiId}`)}
                  >
                    <span className={classes.itemIcon}>{getButtonMappingIcon(mapping)}</span>
                    <span className={classes.itemContent}>
                      <p className={classes.itemTitle}>Button {mapping.buttonIndex}</p>
                      <p className={classes.itemDescription}>
                        {mapping.externalApiName || `API ${mapping.externalApiId}`}
                      </p>
                    </span>
                    <ChevronRight size={15} className={classes.itemChevron} />
                  </button>
                ))}
              </div>
            )}
          </>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs">
      <DialogHeader>
        <DialogTitle>Select measurement type</DialogTitle>
      </DialogHeader>
      <DialogBody>{renderContent()}</DialogBody>
    </Dialog>
  );
};

export default TypePickerDialog;
